(ns app.front.view.escrow.transactions
  (:require [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil]
            [app.front.view.escrow.util :as escrow-util]
            [clojure.string :as str]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::transactions
  (fn [[_ account-id]]
    (rf/subscribe [::entities/entities {:entity/parent account-id
                                        :entity/type   :entity.type/escrow-transaction}]))
  (fn [transactions _]
    (->> transactions
         (sort-by :entity/created)
         reverse)))

(rf/reg-sub ::payments
  (fn [[_ account-id]]
    (rf/subscribe [::entities/entities {:entity/parent account-id
                                        :entity/type   :entity.type/payment}]))
  (fn [payments _]
    (->> payments
         (filter (du/in-state-fn :payment.state/unpaid
                                 :payment.state/pending-confirmation
                                 :payment.state/failed))
         (sort-by :entity/created)
         reverse)))

(rf/reg-sub ::table-items
  (fn [[_ account-id]]
    [(rf/subscribe [::transactions account-id])
     (rf/subscribe [::payments account-id])])
  (fn [[transactions payments] _]
    (->> (concat transactions payments)
         (sort-by :entity/created)
         reverse)))

(rf/reg-sub ::show-widget?
  (fn [[_ account-id]]
    [(rf/subscribe [::transactions account-id])
     (rf/subscribe [::payments account-id])])
  (fn [[transactions payments] _]
    (boolean (or (seq transactions)
                 (seq payments)))))

(rf/reg-event-fx ::set-paid
  (fn [_ [_ payment-id transaction-id]]
    {:fx [[:dispatch [:comm/command-monitored {:command    :fee.escrow/set-escrow-deposit-paid
                                               :data       (util/mmap payment-id transaction-id)
                                               :notify     {:success :escrow.management/set-deposit-paid-success}
                                               :on-success [::set-paid.success]}]]]}))

(rf/reg-event-fx ::set-paid.success
  (fn [{:keys [db]} [_ status resp]]
    {:db (dissoc db ::set-paid-modal-data)
     :fx [[:dispatch [::entities/apply-changes status resp]]]}))

(rf/reg-sub ::set-paid-modal-data :-> ::set-paid-modal-data)

(rf/reg-sub ::set-paid-modal-transaction-id
  :<- [::set-paid-modal-data]
  :-> :modal-transaction-id)

(rf/reg-event-db ::edit-modal-transaction-id
  (rf/path [::set-paid-modal-data :modal-transaction-id])
  (fn [_ [_ tx-id]]
    tx-id))

(rf/reg-sub ::cancel-modal-data :-> ::cancel-modal-data)

(rf/reg-sub ::cancel-reason
  :<- [::cancel-modal-data]
  :-> :cancel-reason)

(rf/reg-event-db ::edit-cancel-reason
  (rf/path [::cancel-modal-data :cancel-reason])
  (fn [_ [_ reason]]
    reason))

(rf/reg-event-fx ::cancel-payment
  (fn [_ [_ payment-id reason]]
    {:comm/command {:command    :fee.escrow/cancel-escrow-deposit
                    :data       (util/mmap payment-id reason)
                    :notify     {:success :escrow.management/cancel-deposit-success}
                    :on-success [::cancel-payment.success]}}))

(rf/reg-event-fx ::cancel-payment.success
  (fn [{:keys [db]} [_ status resp]]
    {:db (dissoc db ::cancel-payment-modal-data)
     :fx [[:dispatch [::entities/apply-changes status resp]]]}))

(defn- cancel-payment-button [tr {:db/keys [id]}]
  [ui/modal-confirmation-button
   (util/mmap {:id                  (str "cancel-unsuccessful-payment-" id)
               :modal-title         (tr [:escrow.management/cancel-deposit-title])
               :on-yes-click        #(>evt [::cancel-payment id (<sub [::cancel-reason])])
               :trigger-button-opts {:kind             :tertiary
                                     :size             :small
                                     :icon             i/remove
                                     :icon-position    :right
                                     :icon-description (tr [:cancel])}
               :trigger-button-text (tr [:cancel])
               :yes-disabled?       (str/blank? (<sub [::cancel-reason]))}
              tr)
   [ui/text-input
    {:label-text (tr [:fees/cancel-bill-reason])
     :on-change  #(>evt [::edit-cancel-reason (futil/tval %)])
     :required   true
     :value      (<sub [::cancel-reason])}]])

(defn- mark-as-paid-modal [tr]
  [ui/text-input
   {:label-text (tr [:fees/transaction-id])
    :on-change  #(>evt [::edit-modal-transaction-id (futil/tval %)])
    :required   true
    :value      (<sub [::set-paid-modal-transaction-id])}])

(defn- set-payment-paid-button [tr {:db/keys [id] :as payment}]
  [ui/modal-confirmation-button
   (util/mmap {:id                  (str "set-payment-" id "-paid")
               :modal-title         (tr [:set-paid])
               :on-yes-click        #(>evt [::set-paid id (<sub [::set-paid-modal-transaction-id])])
               :trigger-button-opts {:kind          :primary
                                     :size          :small
                                     :icon          i/check
                                     :icon-position :right}
               :trigger-button-text (tr [:set-paid])
               :yes-disabled?       (str/blank? (<sub [::set-paid-modal-transaction-id]))}
              tr)
   [mark-as-paid-modal tr payment]])

(defn- description [tr {:payment/keys [additional-payment-info] :escrow-transaction/keys [description]}]
  [:p (or additional-payment-info
          description
          (tr [:escrow.management/empty-details-ph]))])

(defn- admin-action-buttons [tr {:entity/keys [type] :as row}]
  (case type
    :entity.type/payment [:div.flex.gap-x-2
                          [cancel-payment-button tr row]
                          [set-payment-paid-button tr row]]
    nil))

(defn- expand-row [tr row]
  [:<>
   [description tr row]
   (when (<sub [:app.front.view.group.admin.escrow.accounts/is-escrow-admin?])
     [admin-action-buttons tr row])])

(defn- created-col [{:entity/keys [created] :payment/keys [paid-ts]}]
  (or paid-ts
      (date-util/date-time created)))

(defn- by-col [{:payment/keys [payer] :entity/keys [creator]}]
  (->> (or payer creator)
       user-model/full-name))

(defn- amount-col [currency {:entity/keys [type] tx-amount :escrow-transaction/amount p-amount :payment/amount}]
  (case type
    :entity.type/payment            (futil/money-string p-amount currency)
    :entity.type/escrow-transaction (-> tx-amount
                                        str
                                        parse-double
                                        (futil/money-string-whole-units currency))))

(defn- status-col [{:entity/keys [type] :domain/keys [state]}]
  (case type
    :entity.type/payment state
    :entity.type/escrow-transaction :paid))

(defn transactions [{:keys [title account-id transaction-id currency tr]}]
  (when (<sub [::show-widget? account-id])
    [section/subsection
     {:title title}
     [:div.flex.flex-col.gap-6
      [ui-table/modern-responsive-table
       (util/mmap {:cols              [[(tr [:escrow.management/time])
                                        created-col]
                                       [(tr [:escrow.management/by])
                                        by-col]
                                       [(tr [:escrow.management/amount])
                                        (partial amount-col currency)]
                                       [(tr [:escrow.management/status])
                                        status-col]
                                       [(tr [:workspace/workspace])
                                        escrow-util/transaction->workspace-id]]
                   :expanded-func     (partial expand-row tr)
                   :expanded-row-keys (when transaction-id
                                        #{transaction-id})
                   :get-focus?        #(= (:db/id %) transaction-id)
                   :link              {:description escrow-util/transaction->workspace-link-description
                                       :make-href   escrow-util/transaction->workspace-link}
                   :items             (<sub [::table-items account-id])}
                  tr)]]]))
